/* ==========================================================================
   Greasy Miniatures — the roster
   Custom element <gm-roster>. Loaded by sections/greasy-roster.liquid, not by
   theme.liquid: one page's behaviour has no business on the other ninety
   routes.

   The team card on the left never changes. The sheet on the right is a stack
   of <details>, so opening a position, reading it and closing it again is the
   browser's job and not this file's. What is added here is only what a
   disclosure widget cannot do on its own:

     - close the other rows, so the sheet stays a sheet rather than growing
       into seven stacked essays;
     - swap which of a position's models is shown large inside its row;
     - keep the card and the sheet in step — a dot picks the model AND opens
       the row it belongs to, a headshot picks the model AND marks its dot;
     - light a whole position's dots while its row is hovered or focused.

   ONE KEY FOR BOTH HALVES. Every dot and every headshot carries
   data-gm-pick="<row>-<model>" and data-gm-row="<row>". Those two strings are
   the entire contract between the card and the sheet; nothing else here needs
   to know how the other half is built.

   IT IS AN ENHANCEMENT, START TO FINISH
   Every row opens natively, each row shows its first model with no script
   running, and every dot and headshot is a real link to the listing. If this
   file never runs, or throws, the page still reads and still sells — so every
   early return below is a safe one, and a modified click is always left to
   the browser.
   ========================================================================== */

(function () {
  'use strict';

  class GreasyRoster extends HTMLElement {
    connectedCallback() {
      /* The element is re-created wholesale when the editor re-renders the
         section, so this guard is only for a move within the DOM. */
      if (this.dataset.gmBound === 'true') return;

      this.entries = Array.prototype.slice.call(this.querySelectorAll('[data-gm-entry]'));
      this.picks = Array.prototype.slice.call(this.querySelectorAll('[data-gm-pick]'));
      this.shots = Array.prototype.slice.call(this.querySelectorAll('[data-gm-shot]'));
      if (!this.entries.length) return;

      this.dataset.gmBound = 'true';
      var self = this;

      this.entries.forEach(function (entry) {
        /* `toggle` rather than a click handler on the summary: it fires for
           every way a <details> can change, including the keyboard and a
           find-in-page that expands a hidden row, and it fires AFTER the
           state has actually changed. */
        entry.addEventListener('toggle', function () {
          if (entry.open) self.only(entry);
          else self.clear(entry.dataset.gmEntry);
        });

        /* Hovering a row shows where its models stand on the card. It is a
           hint, not a control — a mouse-only nicety with a keyboard
           equivalent on focus, and no touch device gets or needs it. */
        var summary = entry.querySelector('summary');
        if (!summary) return;
        summary.addEventListener('pointerenter', function (event) {
          /* A touch reports as a pointerenter immediately before the tap it
             belongs to, which would flash the dim on every tap. Only a real
             hovering pointer lights anything. */
          if (event.pointerType === 'touch') return;
          self.light(entry.dataset.gmEntry);
        });
        summary.addEventListener('pointerleave', function () {
          self.light(null);
        });
        summary.addEventListener('focus', function () {
          self.light(entry.dataset.gmEntry);
        });
        summary.addEventListener('blur', function () {
          self.light(null);
        });
      });

      this.picks.forEach(function (link) {
        link.addEventListener('click', function (event) {
          /* A modified click is a deliberate "open the listing elsewhere". */
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          self.pick(link.dataset.gmPick);
        });
      });

      /* Escape closes the open row. The test is whether a ROW is open rather
         than what the card is showing: the card never changes now, so there
         is nothing else to read the state off. */
      this.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        var open = self.entries.filter(function (entry) {
          return entry.open;
        });
        if (!open.length) return;
        open.forEach(function (entry) {
          entry.open = false;
        });
        self.light(null);
      });
    }

    /* One row open at a time, and the shot box follows it.

       The picture lives under the team card now, shared by the whole section,
       so opening a row has to FILL it — otherwise a row opened from its own
       summary would expand onto a note about models with no model beside it.
       It shows the one that row was last left on, which is the headshot still
       carrying aria-current, falling back to the first. */
    only(entry) {
      this.entries.forEach(function (other) {
        if (other !== entry) other.open = false;
      });
      var row = entry.dataset.gmEntry;
      var remembered = this.picks.filter(function (link) {
        return (
          link.dataset.gmRow === row &&
          !link.classList.contains('gm-roster__dot') &&
          link.getAttribute('aria-current') === 'true'
        );
      })[0];
      this.pick(remembered ? remembered.dataset.gmPick : row + '-0');
    }

    /* Closing the open row empties the shot box and un-marks its dot. The
       team card on its own is the honest resting state — a model left sitting
       under it with nothing open says a position is selected when none is. */
    clear(row) {
      var open = this.entries.some(function (entry) {
        return entry.open;
      });
      if (open) return;
      this.shots.forEach(function (shot) {
        shot.hidden = true;
      });
      this.picks.forEach(function (link) {
        if (link.classList.contains('gm-roster__dot')) link.removeAttribute('aria-current');
      });
    }

    /* Pick one model: show its picture in its row, mark its headshot and its
       dot, and make sure the row it belongs to is open — which is what turns
       a dot on the card into a way in. */
    pick(key) {
      var showing = String(key);
      var row = showing.split('-')[0];

      /* GUARD AGAINST THE LOOP. Opening a row fires `toggle`, which calls
         only(), which calls pick() — so a pick that opens a row would come
         straight back round. The second pass is harmless but it would undo a
         specific model with the row's remembered one, which is exactly wrong
         when the pick came from a dot. */
      if (this.picking) return;
      this.picking = true;

      var entry = this.entries.filter(function (e) {
        return e.dataset.gmEntry === row;
      })[0];
      if (entry && !entry.open) {
        /* Sets the row open, whose `toggle` closes the others. */
        entry.open = true;
      }

      /* ONE shot box for the whole section now — it lives under the team card,
         not inside the row that selected it — so every other shot is hidden,
         not just this row's. The per-row memory still works because it lives
         in the headshots' aria-current below, which is what reopening a row
         reads back. */
      this.shots.forEach(function (shot) {
        shot.hidden = shot.dataset.gmShot !== showing;
      });

      /* aria-current rather than a class alone: these are links, and "this is
         the one you are looking at" is exactly what it means.

         THE TWO HALVES ARE SCOPED DIFFERENTLY, on purpose. A headshot rail is
         its own set, so every row keeps its own current model and coming back
         to a position finds it as it was left. The CARD is a single surface,
         so only one dot may be current on it — otherwise picking a blitzer
         and then a lineman leaves two filled dots on one photograph, which
         reads as two selections. */
      this.picks.forEach(function (link) {
        var isDot = link.classList.contains('gm-roster__dot');
        if (!isDot && link.dataset.gmRow !== row) return;
        if (link.dataset.gmPick === showing) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      this.light(null);
      this.picking = false;
    }

    /* A whole position's dots at once, while its row is hovered or focused.
       `is-lit` on the dots plus one attribute on the host, which is what dims
       the rest of the card behind them — see greasy-roster.css. */
    light(key) {
      var row = key === null ? null : String(key);
      if (row === null) this.removeAttribute('data-gm-lit');
      else this.setAttribute('data-gm-lit', row);

      this.picks.forEach(function (link) {
        if (link.classList.contains('gm-roster__dot')) {
          link.classList.toggle('is-lit', row !== null && link.dataset.gmRow === row);
        }
      });
    }
  }

  if (!customElements.get('gm-roster')) {
    customElements.define('gm-roster', GreasyRoster);
  }
})();
